/**
 * Displays a single etymology with full detail: title, description, and roots.
 * Uses etymology-root-display for each root.
 */
import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { t } from '../utils/i18n.js'
import './etymology-root-display.js'
import type { EtymologyRootDisplayData } from './etymology-root-display.js'
import styles from './etymology-display.css?inline'

export interface EtymologyDisplayData {
  id: string
  title: string
  description: string
  roots: EtymologyRootDisplayData[]
}

@customElement('etymology-display')
export class EtymologyDisplay extends LitElement {
  /** Etymology data to display */
  @property({ type: Object }) etymology!: EtymologyDisplayData

  /** Show remove button on each root */
  @property({ type: Boolean }) rootsRemovable = false

  /** Root ID that is currently being deleted (disables its remove button) */
  @property({ type: String }) deletingRootId = ''

  /** Label type for roots: 'meaning' or 'word' */
  @property({ type: String }) rootLabelType: 'meaning' | 'word' = 'meaning'

  /** When true, only show roots (no title/description) */
  @property({ type: Boolean }) rootsOnly = false

  static styles = css`${unsafeCSS(styles)}`

  private handleRootRemove(e: CustomEvent) {
    const rootEl = e.target as import('./etymology-root-display.js').EtymologyRootDisplay
    const root = rootEl?.root
    if (root) {
      this.dispatchEvent(new CustomEvent('root-remove', {
        detail: { etymology: this.etymology, root, rootIdForDelete: root.rowId ?? root.id },
        bubbles: true,
        composed: true,
      }))
    }
  }

  render() {
    if (!this.etymology) return html``

    const { title, description, roots } = this.etymology
    const rootsList = roots ?? []
    const showTitleDesc = !this.rootsOnly
    if (this.rootsOnly && rootsList.length === 0) return html``

    return html`
      <div class="etymology-block">
        ${showTitleDesc && title ? html`<div class="etymology-title">${title}</div>` : ''}
        ${showTitleDesc && description ? html`<div class="etymology-description">${description}</div>` : ''}
        ${rootsList.length > 0 ? html`
          <div class="etymology-roots">
            <span class="block-label">${t('createDialog.etymologyRoots')}</span>
            <div class="etymology-roots-list">
              ${rootsList.map((root) => html`
                <etymology-root-display
                  .root=${root}
                  labelType=${this.rootLabelType}
                  ?removable=${this.rootsRemovable}
                  ?removeDisabled=${this.deletingRootId === (root.rowId ?? root.id)}
                  @remove=${this.handleRootRemove}
                ></etymology-root-display>
              `)}
            </div>
          </div>
        ` : ''}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'etymology-display': EtymologyDisplay
  }
}
